import { useEffect, useMemo, useState } from "react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    CheckCircle2, XCircle, Eye, Copy, RefreshCw, ShieldCheck,
    User, FileText, Calendar, AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const STATUS_FILTERS = [
    { key: "all",       label: "All" },
    { key: "submitted", label: "Pending verification" },
    { key: "verified",  label: "Verified" },
    { key: "rejected",  label: "Rejected" },
    { key: "pending",   label: "Awaiting guest" },
];

const BADGE = {
    pending: "bg-slate-100 text-slate-600",
    submitted: "bg-amber-50 text-amber-700",
    verified: "bg-emerald-50 text-emerald-700",
    rejected: "bg-red-50 text-red-700",
};

export default function CheckIns() {
    const [filter, setFilter] = useState("submitted");
    const [rows, setRows] = useState([]);
    const [active, setActive] = useState(null);
    const load = async () => {
        try {
            const { data } = await api.get(`/checkins?status=${filter}`);
            setRows(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    return (
        <>
            <Topbar title="Online check-ins" subtitle="Document scanner · Manual verification" />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="checkins-filters">
                    {STATUS_FILTERS.map((s) => (
                        <button key={s.key} onClick={() => setFilter(s.key)}
                                data-testid={`ci-filter-${s.key}`}
                                className={`rounded-md border px-3 py-1.5 text-[12px] font-medium ${
                                    filter === s.key ? "border-[#ff7b02] bg-[#ff7b02]/10 text-[#ff7b02]" :
                                    "border-border text-muted-foreground hover:bg-muted/40"
                                }`}>
                            {s.label}
                        </button>
                    ))}
                    <button onClick={load} className="akm-btn-ghost text-[11px] ml-auto">
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                <div className="akm-card overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left">Guest</th>
                                <th className="px-3 py-2 text-left">Reservation</th>
                                <th className="px-3 py-2 text-left">Stay</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-left">Submitted</th>
                                <th className="px-3 py-2 text-left">Documents</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody data-testid="checkins-table">
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                                    <td className="px-3 py-2 font-medium">
                                        {r.personal_details?.first_name || r.reservation?.guest?.split(" ")[0] || "—"}{" "}
                                        {r.personal_details?.last_name || r.reservation?.guest?.split(" ")[1] || ""}
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground font-mono text-[11px]">
                                        {r.reservation?.code || r.reservation_id?.slice(0, 8)}
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground">
                                        {r.reservation?.check_in} → {r.reservation?.check_out}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${BADGE[r.status] || "bg-slate-100"}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground text-[11px]">
                                        {r.submitted_at ? formatDateTime(r.submitted_at) : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground">{r.documents?.length || 0}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={() => setActive(r.id)} className="akm-btn-ghost text-[11px]"
                                                data-testid={`ci-open-${r.id}`}>
                                            <Eye size={12} /> Review
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan="7" className="py-10 text-center text-muted-foreground">
                                    No check-ins for this filter
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {active && <CheckInDrawer id={active} onClose={() => setActive(null)} onChanged={load} />}
        </>
    );
}

function CheckInDrawer({ id, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [doc, setDoc] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => setData((await api.get(`/checkins/${id}`)).data);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const previewDoc = async (docId) => {
        try {
            const { data } = await api.get(`/checkins/${id}/documents/${docId}/image`);
            setDoc({ id: docId, ...data });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const verify = async () => {
        setBusy(true);
        try {
            await api.post(`/checkins/${id}/verify`, { note });
            toast.success("Check-in verified");
            await onChanged();
            onClose();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const reject = async () => {
        if (!window.confirm("Reject this check-in?")) return;
        setBusy(true);
        try {
            await api.post(`/checkins/${id}/reject`, { note });
            toast.success("Check-in rejected");
            await onChanged();
            onClose();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="ci-drawer">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[760px] flex-col overflow-y-auto border-l border-border bg-background shadow-2xl">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                    <div>
                        <div className="text-[13px] font-semibold tracking-tight">Online check-in</div>
                        <div className="text-[11px] text-muted-foreground">Manual verification</div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground"><X size={16} /></button>
                </div>
                {!data ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : (
                    <div className="p-5 space-y-4">
                        <Section icon={<ShieldCheck size={14} />} title="Status">
                            <div className="flex items-center gap-2">
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${BADGE[data.status]}`}>
                                    {data.status}
                                </span>
                                {data.policies_accepted && <span className="text-[11px] text-emerald-700">✓ Policies accepted</span>}
                                {data.signature_data_url && <span className="text-[11px] text-emerald-700">✓ Signed</span>}
                            </div>
                            {data.signature_data_url && (
                                <img src={data.signature_data_url} alt="Signature"
                                     className="mt-2 max-h-24 rounded-md border border-border bg-white" />
                            )}
                        </Section>

                        <Section icon={<User size={14} />} title="Personal details">
                            <Grid>
                                <Cell label="First name" value={data.personal_details?.first_name} />
                                <Cell label="Last name" value={data.personal_details?.last_name} />
                                <Cell label="Email" value={data.personal_details?.email} />
                                <Cell label="Phone" value={data.personal_details?.phone} />
                                <Cell label="Country" value={data.personal_details?.country} />
                                <Cell label="Arrival time" value={data.arrival_time} />
                                <Cell label="City" value={data.personal_details?.city} />
                                <Cell label="Additional guests" value={data.personal_details?.companion_count} />
                                <Cell label="Address" wide value={data.personal_details?.address} />
                                {data.personal_details?.notes && <Cell label="Notes" wide value={data.personal_details.notes} />}
                            </Grid>
                        </Section>

                        <Section icon={<FileText size={14} />} title={`Documents (${(data.documents || []).length})`}>
                            {(data.documents || []).map((d) => (
                                <div key={d.id} className="rounded-md border border-border p-3 mb-2" data-testid={`ci-doc-row-${d.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-[12.5px] font-semibold capitalize">{d.doc_type?.replace("_", " ")}</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                Uploaded {formatDateTime(d.uploaded_at)} ·
                                                Confidence {Math.round((d.fields?.confidence || 0) * 100)}% ·
                                                Provider {d.fields?.provider || "mock"}
                                            </div>
                                        </div>
                                        <button onClick={() => previewDoc(d.id)} className="akm-btn-ghost text-[11px]"
                                                data-testid={`ci-preview-${d.id}`}>
                                            <Eye size={12} /> Preview
                                        </button>
                                    </div>
                                    <Grid>
                                        <Cell label="Document #" value={d.fields?.document_number} />
                                        <Cell label="First name" value={d.fields?.first_name} />
                                        <Cell label="Last name" value={d.fields?.last_name} />
                                        <Cell label="Date of birth" value={d.fields?.date_of_birth} />
                                        <Cell label="Nationality" value={d.fields?.nationality} />
                                        <Cell label="Expiry date" value={d.fields?.expiry_date} />
                                    </Grid>
                                </div>
                            ))}
                            {(data.documents || []).length === 0 && (
                                <div className="text-[12px] text-muted-foreground">No documents uploaded</div>
                            )}
                        </Section>

                        {data.status === "submitted" && (
                            <Section icon={<CheckCircle2 size={14} />} title="Verification">
                                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                                          placeholder="Optional note (e.g. requested original at arrival)"
                                          rows={2} className="akm-input w-full text-[13px]"
                                          data-testid="ci-verify-note" />
                                <div className="mt-2 flex justify-end gap-2">
                                    <button onClick={reject} disabled={busy} className="akm-btn-ghost text-[12px] text-red-700"
                                            data-testid="ci-reject">
                                        <XCircle size={12} /> Reject
                                    </button>
                                    <button onClick={verify} disabled={busy} className="akm-btn-primary text-[12px]"
                                            data-testid="ci-verify">
                                        <CheckCircle2 size={12} /> Verify
                                    </button>
                                </div>
                            </Section>
                        )}

                        {data.status === "rejected" && data.verification_note && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12px] text-red-800">
                                <strong>Rejection note:</strong> {data.verification_note}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {doc && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="ci-doc-preview">
                    <div className="absolute inset-0 bg-foreground/70" onClick={() => setDoc(null)} />
                    <div className="relative max-h-[90vh] max-w-[800px] rounded-xl border border-border bg-background p-3 shadow-2xl">
                        <button onClick={() => setDoc(null)} className="absolute right-2 top-2 z-10 rounded-full bg-card p-1.5 text-muted-foreground">
                            <X size={14} />
                        </button>
                        <img src={doc.image_b64} alt={doc.file_name}
                             className="max-h-[85vh] max-w-full rounded-md object-contain" />
                        <div className="mt-1 text-center text-[11px] text-muted-foreground">{doc.file_name}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Section({ icon, title, children }) {
    return (
        <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {icon}{title}
            </div>
            {children}
        </div>
    );
}
function Grid({ children }) {
    return <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12.5px]">{children}</div>;
}
function Cell({ label, value, wide }) {
    return (
        <div className={wide ? "col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-[12.5px] text-foreground">{value || "—"}</div>
        </div>
    );
}
